import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { zodResolver } from "@hookform/resolvers/zod";
import { Immutable, castImmutable } from "immer";
import { atom, useAtomValue, useStore } from "jotai";
import { Trash } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";
import { useMemoOne } from "use-memo-one";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { TournamentLayoutDetailQuery } from "@/gen/graphql";
import { CmsBlock, CmsLayout, zCmsLayout } from "@/gen/realtime";
import { ContentEditor } from "@/routes/layouts/components/ContentEditor";
import { useDragAndDrop } from "@/routes/layouts/dragAndDrop";
import { setupKeyboardHandlers } from "@/routes/layouts/keyboardHandlers";
import { initialState, updateState } from "@/routes/layouts/layoutEditState";
import { Action } from "@/routes/layouts/layoutEditState";
import { zLayoutMetadataValues } from "@/routes/layouts/layoutMetadata";
import { UndoHandlerProvider, useUndoHandler } from "@/routes/layouts/undoHandler";
import { cn } from "@/utils";

import { TournamentLayoutEditSidebar } from "./components/TournamentLayoutEditSidebar";

// Validation schema for tournament layout metadata
const zTournamentLayoutMetadata = zLayoutMetadataValues.extend({
    tournamentPhase: z.enum(["BEFORE", "DURING", "AFTER"]),
});

export type TournamentLayoutMetadata = z.infer<typeof zTournamentLayoutMetadata>;

export const tournamentLayoutDetailQuery = graphql(`
    query TournamentLayoutDetail($layoutId: UUID!) {
        tournamentLayout(id: $layoutId) {
            id
            content
            name
            tournamentPhase
            publishAt
            unpublishAt
            createdAt
            updatedAt
            tournamentId
        }
    }
`);

export const updateTournamentLayoutMutation = graphql(`
    mutation UpdateTournamentLayout($input: UpdateTournamentLayoutInput!) {
        updateTournamentLayout(input: $input) {
            id
            content
            publishAt
        }
    }
`);

export default function TournamentLayoutDetailRoute() {
    const { layoutId } = useParams<{ layoutId: string }>();

    const [result] = useQuery({
        query: tournamentLayoutDetailQuery,
        variables: {
            layoutId: layoutId!,
        },
    });

    if (result.error) {
        return <div>{result.error.toString()}</div>;
    }

    if (!result.data) {
        return <div>Loading...</div>;
    }

    if (!result.data.tournamentLayout) {
        return <div>Tournament layout not found</div>;
    }

    return (
        <UndoHandlerProvider>
            <TournamentLayoutDetailView layout={result.data.tournamentLayout} />
        </UndoHandlerProvider>
    );
}

function TournamentLayoutDetailView({
    layout,
}: {
    layout: NonNullable<TournamentLayoutDetailQuery["tournamentLayout"]>;
}) {
    const metadataForm = useForm<TournamentLayoutMetadata>({
        defaultValues: {
            publishAt: layout.publishAt,
            name: layout.name,
            tournamentPhase: layout.tournamentPhase || "DURING",
        },
        mode: "onSubmit",
        resolver: zodResolver(zTournamentLayoutMetadata),
    });

    const [_updateLayoutRes, updateLayout] = useMutation(updateTournamentLayoutMutation);

    const { onPatches, undo, flushChanges } = useUndoHandler();

    const store = useStore();
    const stateAtom = useMemoOne(() => atom(castImmutable(initialState())), []);
    const state = useAtomValue(stateAtom);

    const dispatch = useCallback(
        (action: Action) => {
            const oldValue = store.get(stateAtom);
            const newValue = updateState(oldValue, action, store, onPatches);
            store.set(stateAtom, newValue);
            flushChanges();
        },
        [store, stateAtom, onPatches, flushChanges]
    );

    // Setup keyboard handlers (undo/redo, copy/paste)
    useEffect(() => {
        return setupKeyboardHandlers({
            store,
            stateAtom,
            dispatch,
            undo,
        });
    }, [dispatch, stateAtom, store, undo]);

    // Setup drag and drop
    const { isDragging } = useDragAndDrop(dispatch);

    // Load initial content
    useEffect(() => {
        if (layout.content) {
            dispatch({
                type: "set-content",
                content: zCmsLayout.parse(layout.content),
            });
        }
    }, [layout.content, dispatch]);

    const save = () => {
        metadataForm.handleSubmit(async metadata => {
            const blocks: Record<string, Immutable<CmsBlock>> = {};

            for (const blockId of Object.keys(state.layout.blocks)) {
                blocks[blockId] = store.get(state.layout.blocks[blockId]!);
            }

            const content: Immutable<CmsLayout> = { ...state.layout, blocks };
            const result = await updateLayout({
                input: {
                    id: layout.id,
                    content,
                    ...metadata,
                },
            });

            if (result.error) {
                toast.error("Failed to save tournament layout", {
                    description: result.error.message,
                });
            } else {
                toast.success("Tournament layout saved successfully");
            }
        })();
    };

    if (!state) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <div className="mx-4 flex items-center gap-3">
                <Button onClick={save}>Save</Button>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button variant="secondary">Preview</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogTitle>Preview Tournament Layout</DialogTitle>
                        <DialogHeader>
                            <DialogDescription className="flex flex-col items-center">
                                <QRCode
                                    size={400}
                                    value={`taketaketake://preview-tournament-layout/${layout.id}`}
                                />
                                <div className="flex flex-col gap-2">
                                    <a
                                        href={`taketaketake://preview-tournament-layout/${layout.id}`}
                                    >
                                        taketaketake://preview-tournament-layout/{layout.id}
                                    </a>
                                    <Button variant="secondary" asChild>
                                        <a
                                            href={`https://taketaketake.com/preview-tournament-layout/${layout.id}`}
                                        >
                                            Preview on web
                                        </a>
                                    </Button>
                                </div>
                            </DialogDescription>
                        </DialogHeader>
                    </DialogContent>
                </Dialog>
                {isDragging && <DragToDelete />}
                <Button
                    onClick={() => {
                        dispatch({
                            type: "set-active-block",
                            blockId: null,
                        });
                    }}
                    className="ml-auto"
                >
                    Metadata
                </Button>
            </div>
            <div className="m-2 flex flex-row gap-4">
                <ContentEditor
                    dispatch={dispatch}
                    state={state}
                    blocks={state.layout.order}
                    depth={0}
                    parentId={null}
                    allParents={[]}
                />
                <TournamentLayoutEditSidebar
                    state={state}
                    dispatch={dispatch}
                    metadataForm={metadataForm}
                    onSubmit={save}
                />
            </div>
        </div>
    );
}

const DragToDelete = () => {
    const element = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    useEffect(() => {
        return dropTargetForElements({
            element: element.current!,
            getData() {
                return { t: "delete" };
            },
            onDragEnter: () => {
                setActive(true);
            },
            onDragLeave() {
                setActive(false);
            },
            onDrop() {
                setActive(false);
            },
        });
    });

    return (
        <div
            ref={element}
            className={cn(
                "mx-4 flex items-center gap-3 rounded bg-red-300 p-2",
                active ? "bg-red-600" : "bg-red-300"
            )}
        >
            <Trash /> Drag here to delete
        </div>
    );
};
